import equal from 'fast-deep-equal'
import { DesignPersonalizationContextService as DPCS } from '~/services/DesignPersonalizationContextService'
// TODO: fix this import: currnetly this is a hack because this type isn't exported from react-brand
import { BrandProfileContextType } from '@vp/react-brand/dist/esm/brandProfileProvider'
import type { DesignPersonalizationContext } from '@vp/personalization-types'
import { SelectedAsset, RenderableTextField, Scheme, SchemesStore } from './types'
import { createNewDpc, dpcToBrandContent } from '../../util'

export interface UpdateSchemeParams {
  schemeId: string
  images: SelectedAsset[]
  textFields: RenderableTextField[]
  shouldUpdateBrand?: boolean
}

export interface LoadSchemeParams {
  schemeId: string
  sessionId: string
}

export interface LoadBrandSchemeParams {
  schemeId: string
  images: SelectedAsset[]
  textFields: RenderableTextField[]
  brandId: string
  storageKey: string
  sessionId?: string
}

export interface CreateSchemeParams {
  schemeId: string
  images: SelectedAsset[]
  textFields: RenderableTextField[]
  storageKey: string
  shouldCreateBrand?: boolean
}

export type Action =
  | { type: 'ADD_SCHEME'; payload: { schemeId: string; scheme: Scheme } }
  | { type: 'REMOVE_SCHEME'; payload: { schemeId: string } }
  | { type: 'UPDATE_SCHEMES_CONTEXT'; payload: { schemeId: string; contextData: DesignPersonalizationContext; contextId: string } }
  | { type: 'SET_ACTIVE_SCHEME'; payload: { schemeId: string | undefined } }
  | { type: 'MARK_SCHEME_UPDATING'; payload: { schemeId: string } }
  | { type: 'UNMARK_SCHEME_UPDATING'; payload: { schemeId: string } }

export interface SchemeActionSet {
  updateScheme: (params: UpdateSchemeParams) => Promise<void>
  loadScheme: (params: LoadSchemeParams) => Promise<void>
  loadBrandScheme: (params: LoadBrandSchemeParams) => Promise<void>
  createScheme: (params: CreateSchemeParams) => Promise<void>
  setActiveScheme: (id?: string) => void
}

interface InitializeSchemesStoreActions {
  state: SchemesStore
  dispatch: React.Dispatch<Action>
  brandContext: BrandProfileContextType
  accessToken: string
  defaultToPhoto?: boolean
}

export const initializeActions = ({ state, dispatch, brandContext, accessToken, defaultToPhoto = false }: InitializeSchemesStoreActions): SchemeActionSet => {
  const updateScheme = async (params: UpdateSchemeParams): Promise<void> => {
    const { schemeId, images, textFields, shouldUpdateBrand } = params

    const scheme = state.schemes[schemeId]
    const prevDpc = scheme.designPersonalizationContext.designPersonalizationData
    const newDpc = await createNewDpc(textFields, images, defaultToPhoto, prevDpc)

    if (equal(prevDpc, newDpc)) return

    dispatch({ type: 'MARK_SCHEME_UPDATING', payload: { schemeId } })

    const { designPersonalizationContext, brandId } = await DPCS.updateDesignPersonalizationSession(scheme.sessionId, newDpc, undefined, accessToken)

    const { designPersonalizationData, designPersonalizationContextId } = designPersonalizationContext

    if (brandId && shouldUpdateBrand) {
      const brandData = dpcToBrandContent(designPersonalizationData)

      brandContext.api.updateBrand({
        brandId,
        isActive: false,
        isFactual: true,
        ...brandData,
      })
    }

    dispatch({
      type: 'UPDATE_SCHEMES_CONTEXT',
      payload: {
        schemeId,
        contextData: designPersonalizationData,
        contextId: designPersonalizationContextId,
      },
    })

    dispatch({ type: 'UNMARK_SCHEME_UPDATING', payload: { schemeId } })
  }

  const loadScheme = async (params: LoadSchemeParams): Promise<void> => {
    const { schemeId, sessionId } = params

    const { brandId, designPersonalizationContext } = await DPCS.getDesignPersonalizationSession(sessionId, accessToken)

    const scheme: Scheme = {
      brandId,
      sessionId,
      designPersonalizationContext: {
        ...designPersonalizationContext,
        designPersonalizationTextOverrides: [],
      },
    }

    dispatch({ type: 'ADD_SCHEME', payload: { schemeId, scheme } })
  }

  // TODO: Brand, and other schemes, have their own initiaization actions or case paths
  const loadBrandScheme = async (params: LoadBrandSchemeParams): Promise<void> => {
    const { schemeId, images, textFields, brandId, storageKey, sessionId } = params

    const dpc = await createNewDpc(textFields, images, defaultToPhoto)

    if (!sessionId) {
      const {
        brandId: responseBrandId,
        designPersonalizationContext,
        designPersonalizationSessionId,
      } = await DPCS.createDesignPersonalizationSession(dpc, brandId, accessToken)

      const scheme: Scheme = {
        brandId: responseBrandId,
        sessionId: designPersonalizationSessionId,
        designPersonalizationContext: {
          designPersonalizationContextId: designPersonalizationContext.designPersonalizationContextId,
          designPersonalizationData: designPersonalizationContext.designPersonalizationData,
          designPersonalizationTextOverrides: [],
        },
      }

      localStorage.setItem(storageKey, designPersonalizationSessionId)

      dispatch({ type: 'ADD_SCHEME', payload: { schemeId, scheme } })
      return
    }

    const {
      brandId: responseBrandId,
      designPersonalizationContext,
      designPersonalizationSessionId,
    } = await DPCS.updateDesignPersonalizationSession(sessionId, dpc, brandId, accessToken)

    const scheme: Scheme = {
      brandId: responseBrandId,
      sessionId: designPersonalizationSessionId,
      designPersonalizationContext: {
        designPersonalizationContextId: designPersonalizationContext.designPersonalizationContextId,
        designPersonalizationData: designPersonalizationContext.designPersonalizationData,
        designPersonalizationTextOverrides: [],
      },
    }

    dispatch({ type: 'ADD_SCHEME', payload: { schemeId, scheme } })
  }

  const createScheme = async (params: CreateSchemeParams): Promise<void> => {
    const { schemeId, images, textFields, storageKey, shouldCreateBrand } = params

    const dpc = await createNewDpc(textFields, images, defaultToPhoto)
    let newBrand

    if (shouldCreateBrand) {
      const brandData = dpcToBrandContent(dpc)

      newBrand = await brandContext.api.createBrand(brandData, false)
    }

    const {
      brandId: responseBrandId,
      designPersonalizationContext,
      designPersonalizationSessionId,
    } = await DPCS.createDesignPersonalizationSession(dpc, newBrand?.brandId, accessToken)

    const scheme: Scheme = {
      brandId: responseBrandId,
      sessionId: designPersonalizationSessionId,
      designPersonalizationContext: {
        designPersonalizationContextId: designPersonalizationContext.designPersonalizationContextId,
        designPersonalizationData: designPersonalizationContext.designPersonalizationData,
        designPersonalizationTextOverrides: [],
      },
    }

    localStorage.setItem(storageKey, designPersonalizationSessionId)

    dispatch({ type: 'ADD_SCHEME', payload: { schemeId, scheme } })
  }

  const setActiveScheme = (schemeId: string | undefined): void => {
    dispatch({ type: 'SET_ACTIVE_SCHEME', payload: { schemeId } })
  }

  return {
    updateScheme,
    loadScheme,
    loadBrandScheme,
    createScheme,
    setActiveScheme,
  }
}
